import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ApiError } from '@/api/client'
import { LoadingScreen } from '@/components/LoadingScreen'
import {
  type FootballMatch,
  getMembershipStatus,
  inferPouleUxStageType,
  usePouleDashboardBootstrap,
  useInfinitePouleMatches,
  usePouleInfo,
  useJoinPoule,
  usePouleLiveMatches,
  usePouleStages,
  useSavePoulePrediction,
} from '@/features/poules/api'
import { PouleDashboardTab } from '@/features/poules/components/PouleDashboardTab'
import { PouleDetailHero } from '@/features/poules/components/PouleDetailHero'
import { PouleMembershipState } from '@/features/poules/components/PouleMembershipState'
import {
  canPredictMatch,
  type MatchCardAction,
  PouleMatchesTab,
} from '@/features/poules/components/PouleMatchesTab'
import {
  type PouleTabItem,
  PouleTabs,
} from '@/features/poules/components/PouleTabs'
import { PoulePlaceholderTab } from '@/features/poules/components/PoulePlaceholderTab'
import { PoulePredictionModal } from '@/features/poules/components/PoulePredictionModal'
import { PouleRankingTab } from '@/features/poules/components/PouleRankingTab'
import { getKickoffTimeValue } from '@/lib/kickoff'
import { useTranslation } from '@/i18n'

function getPouleTabs(t: (key: string) => string, isGroup: boolean): PouleTabItem[] {
  const tabs: PouleTabItem[] = [
    { id: 'dashboard', label: t('Dashboard') },
    ...(isGroup ? [{ id: 'groups', label: t('Groups'), isPlaceholder: true }] : []),
    { id: 'matches', label: t('Wedstrijden voorspellen') },
    { id: 'results', label: t('Uitslagen') },
    { id: 'ranking', label: t('Ranking') },
  ]
  return tabs
}

const PLACEHOLDER_COPY: Record<string, { title: string; body: string }> = {
  groups: {
    title: 'Groups volgt in fase 2',
    body: 'De groepsindeling en tussenstanden komen hier terug zodra we de group-stage endpoints aansluiten.',
  },
  ranking: {
    title: 'Ranking volgt later',
    body: 'Dit onderdeel wordt in een volgende fase uitgebreid.',
  },
}

export function PouleDetailPage() {
  const { t } = useTranslation()
  const { pouleId } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const numericPouleId = parsePouleId(pouleId)
  const [selectedPredictionMatch, setSelectedPredictionMatch] = useState<FootballMatch | null>(null)
  const infoQuery = usePouleInfo(numericPouleId)
  const membershipStatus = getMembershipStatus(infoQuery.data?.membership)
  const joinPoule = useJoinPoule(numericPouleId)
  const isAcceptedMember = membershipStatus === 'accepted'
  const canManagePoule =
    isAcceptedMember &&
    infoQuery.data?.poule.audience_type === 'company' &&
    ['admin', 'owner'].includes(infoQuery.data?.membership?.role ?? '')
  const isCompanyLicenseRequired =
    infoQuery.data?.poule.audience_type === 'company' &&
    infoQuery.data?.poule.license_required === true
  const stagesQuery = usePouleStages(
    numericPouleId,
    infoQuery.isSuccess && isAcceptedMember && !isCompanyLicenseRequired,
  )
  const uxStageType = inferPouleUxStageType(stagesQuery.data)
  const tabs = getPouleTabs(t, uxStageType === 'group')
  const requestedTab = normalizeTabValue(searchParams.get('tab'))
  const activeTab = useMemo(
    () => (tabs.some((tab) => tab.id === requestedTab) ? requestedTab : 'dashboard'),
    [requestedTab, tabs],
  )
  const dashboardQuery = usePouleDashboardBootstrap(
    numericPouleId,
    infoQuery.isSuccess && isAcceptedMember && stagesQuery.isSuccess,
  )
  const liveMatchesQuery = usePouleLiveMatches(
    numericPouleId,
    infoQuery.isSuccess &&
      isAcceptedMember &&
      stagesQuery.isSuccess &&
      activeTab === 'dashboard',
  )
  const futureMatchesQuery = useInfinitePouleMatches(
    numericPouleId,
    'future',
    infoQuery.isSuccess &&
      isAcceptedMember &&
      stagesQuery.isSuccess &&
      (activeTab === 'matches' || activeTab === 'dashboard'),
  )
  const pastMatchesQuery = useInfinitePouleMatches(
    numericPouleId,
    'past',
    infoQuery.isSuccess &&
      isAcceptedMember &&
      stagesQuery.isSuccess &&
      activeTab === 'results',
  )
  const savePrediction = useSavePoulePrediction(numericPouleId)
  const futureMatches = useMemo(
    () =>
      [...(futureMatchesQuery.data?.pages ?? []).flatMap((page) => page.data)].sort(
        (left, right) =>
          getKickoffTimeValue(left.kickoff_utc, left.kickoff_nl) -
          getKickoffTimeValue(right.kickoff_utc, right.kickoff_nl),
      ),
    [futureMatchesQuery.data],
  )

  useEffect(() => {
    if (requestedTab !== activeTab) {
      setSearchParams(
        (currentParams) => {
          const nextParams = new URLSearchParams(currentParams)
          if (activeTab === 'dashboard') {
            nextParams.delete('tab')
          } else {
            nextParams.set('tab', activeTab)
          }
          return nextParams
        },
        { replace: true },
      )
    }
  }, [activeTab, requestedTab, setSearchParams])

  useEffect(() => {
    setSelectedPredictionMatch(null)
    savePrediction.reset()
  }, [activeTab])

  function handleTabChange(nextTab: string) {
    const normalizedTab = normalizeTabValue(nextTab)

    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams)
        if (normalizedTab === 'dashboard') {
          nextParams.delete('tab')
        } else {
          nextParams.set('tab', normalizedTab)
        }
        return nextParams
      },
      { replace: false },
    )
  }

  if (numericPouleId == null) {
    return (
      <InlineStatePanel
        title={t('Ongeldige poule')}
        body={t('De URL bevat geen geldig poule-id.')}
        tone="error"
      />
    )
  }

  if (infoQuery.isLoading) {
    return (
      <div className="space-y-6">
        <LoadingScreen compact />
        <PouleDetailSkeleton />
      </div>
    )
  }

  if (infoQuery.isError || !infoQuery.data) {
    return (
      <InlineStatePanel
        title={
          infoQuery.error instanceof ApiError && infoQuery.error.status === 404
            ? t('Poule niet gevonden')
            : t('Poule niet geladen')
        }
        body={
          infoQuery.error instanceof ApiError && infoQuery.error.status === 404
            ? t('Deze poule bestaat niet of is niet meer beschikbaar.')
            : t('Controleer je sessie en probeer de poule opnieuw te openen.')
        }
        tone="error"
        cta={t('Opnieuw proberen')}
        onClick={() => void infoQuery.refetch()}
      />
    )
  }

  const { poule, member_count: memberCount } = infoQuery.data

  if (!isAcceptedMember) {
    return (
      <div className="space-y-5">
        <PouleDetailHero
          poule={poule}
          memberCount={memberCount}
          pouleId={numericPouleId}
          canInvite={isAcceptedMember}
          canManage={canManagePoule}
        />
        <PouleMembershipState
          status={membershipStatus}
          requiresApproval={poule.requires_approval === true}
          isJoining={joinPoule.isPending}
          errorMessage={joinPoule.error?.message}
          onJoin={() => joinPoule.mutate()}
        />
      </div>
    )
  }

  if (isCompanyLicenseRequired && canManagePoule) {
    return (
      <div className="space-y-5">
        <PouleDetailHero
          poule={poule}
          memberCount={memberCount}
          pouleId={numericPouleId}
          canInvite={false}
          canManage={canManagePoule}
        />
        <section className="rounded-[1.5rem] border border-amber-300/20 bg-amber-300/[0.06] p-5 sm:p-6">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-200">{t('Licentie nodig')}</p>
          <h2 className="mt-2 text-2xl font-black tracking-tight text-white">{t('Er is nog geen licentie actief')}</h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-surface-300">
            {t('Kies eerst een licentie om deelnemers uit te nodigen en de bedrijfsfuncties te gebruiken.')}
          </p>
          <Link
            to={'/poules/' + numericPouleId + '/licentie'}
            className="mt-5 inline-flex rounded-xl bg-accent-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-accent-500"
          >
            {t('Licentie kiezen')}
          </Link>
        </section>
      </div>
    )
  }

  if (stagesQuery.isLoading) {
    return (
      <div className="space-y-5">
        <PouleDetailHero
          poule={poule}
          memberCount={memberCount}
          pouleId={numericPouleId}
          canInvite={isAcceptedMember}
          canManage={canManagePoule}
        />
        <PouleTabs items={tabs} activeTab={activeTab} onChange={handleTabChange} />
        <PouleDashboardSkeleton />
      </div>
    )
  }

  if (stagesQuery.isError) {
    return (
      <div className="space-y-5">
        <PouleDetailHero
          poule={poule}
          memberCount={memberCount}
          pouleId={numericPouleId}
          canInvite={isAcceptedMember}
          canManage={canManagePoule}
        />
        <InlineStatePanel
          title={t('Stages niet geladen')}
          body={t('De fases van deze poule konden niet worden geladen.')}
          tone="error"
          cta={t('Opnieuw proberen')}
          onClick={() => void stagesQuery.refetch()}
        />
      </div>
    )
  }

  return (
    <>
      <div className="space-y-5">
        <PouleDetailHero
          poule={poule}
          memberCount={memberCount}
          pouleId={numericPouleId}
          canInvite={isAcceptedMember}
          canManage={canManagePoule}
        />

        <PouleTabs items={tabs} activeTab={activeTab} onChange={handleTabChange} />

        {activeTab === 'dashboard' ? (
          <DashboardTabState
            pouleId={numericPouleId}
            isLoading={dashboardQuery.isLoading}
            isError={dashboardQuery.isError}
            onRetry={() => void dashboardQuery.refetch()}
            bootstrap={dashboardQuery.data}
            liveMatches={liveMatchesQuery.data ?? []}
            upcomingMatches={futureMatchesQuery.data?.pages[0]?.data ?? []}
            liveMatchesState={
              liveMatchesQuery.isLoading
                ? 'loading'
                : liveMatchesQuery.isError
                  ? 'error'
                  : dashboardQuery.data
                  ? 'ready'
                  : 'idle'
            }
            upcomingMatchesState={
              futureMatchesQuery.isLoading
                ? 'loading'
                : futureMatchesQuery.isError
                  ? 'error'
                  : futureMatchesQuery.data
                    ? 'ready'
                    : 'idle'
            }
            onSelectUpcomingMatch={(match) => {
              savePrediction.reset()
              setSelectedPredictionMatch(match)
            }}
            onOpenRankingTab={() => handleTabChange('ranking')}
            memberCount={memberCount}
          />
        ) : null}

        {activeTab === 'matches' ? (
          <PouleMatchesTab
            status="future"
            pages={futureMatchesQuery.data?.pages}
            isLoading={futureMatchesQuery.isLoading}
            isError={futureMatchesQuery.isError}
            isFetchingNextPage={futureMatchesQuery.isFetchingNextPage}
            hasNextPage={futureMatchesQuery.hasNextPage === true}
            onRetry={() => void futureMatchesQuery.refetch()}
            onLoadMore={() => {
              if (!futureMatchesQuery.hasNextPage || futureMatchesQuery.isFetchingNextPage) return
              void futureMatchesQuery.fetchNextPage()
            }}
            onSelectMatch={(action) =>
              handleMatchSelection(
                action,
                numericPouleId,
                activeTab,
                navigate,
                setSelectedPredictionMatch,
              )
            }
          />
        ) : null}

        {activeTab === 'results' ? (
          <PouleMatchesTab
            status="past"
            pages={pastMatchesQuery.data?.pages}
            isLoading={pastMatchesQuery.isLoading}
            isError={pastMatchesQuery.isError}
            isFetchingNextPage={pastMatchesQuery.isFetchingNextPage}
            hasNextPage={pastMatchesQuery.hasNextPage === true}
            onRetry={() => void pastMatchesQuery.refetch()}
            onLoadMore={() => {
              if (!pastMatchesQuery.hasNextPage || pastMatchesQuery.isFetchingNextPage) return
              void pastMatchesQuery.fetchNextPage()
            }}
            onSelectMatch={(action) =>
              handleMatchSelection(
                action,
                numericPouleId,
                activeTab,
                navigate,
                setSelectedPredictionMatch,
              )
            }
          />
        ) : null}

        {activeTab === 'ranking' ? (
          <PouleRankingTab
            pouleId={numericPouleId}
            supportsTeams={poule.supports_teams === true}
          />
        ) : null}

        {!['dashboard', 'matches', 'results', 'ranking'].includes(activeTab) ? (
          <PoulePlaceholderTab
            title={t(PLACEHOLDER_COPY[activeTab]?.title || 'Onderdeel volgt later')}
            body={
              PLACEHOLDER_COPY[activeTab]?.body ||
              t('Dit onderdeel wordt in een volgende fase ingevuld.')
            }
          />
        ) : null}
      </div>

      {selectedPredictionMatch ? (
        <PoulePredictionModal
          match={selectedPredictionMatch}
          isSaving={savePrediction.isPending}
          errorMessage={savePrediction.error?.message}
          onClose={() => {
            if (savePrediction.isPending) return
            savePrediction.reset()
            setSelectedPredictionMatch(null)
          }}
          onSave={(homePred, awayPred) => {
            if (numericPouleId == null) return
            const currentMatchId = selectedPredictionMatch.id
            const nextMatch = getNextPoulePredictionMatch(
              futureMatches,
              currentMatchId,
            )

            savePrediction.mutate(
              {
                matchId: currentMatchId,
                pouleId: numericPouleId,
                homePred,
                awayPred,
              },
              {
                onSuccess: async () => {
                  savePrediction.reset()
                  setSelectedPredictionMatch(nextMatch)

                  const refreshedMatches = await futureMatchesQuery.refetch()
                  if (!nextMatch) return

                  const freshNextMatch = refreshedMatches.data?.pages
                    .flatMap((page) => page.data)
                    .find((match) => match.id === nextMatch.id)
                  if (freshNextMatch) setSelectedPredictionMatch(freshNextMatch)
                },
              },
            )
          }}
        />
      ) : null}
    </>
  )
}

function DashboardTabState({
  pouleId,
  isLoading,
  isError,
  onRetry,
  bootstrap,
  liveMatches,
  upcomingMatches,
  liveMatchesState,
  upcomingMatchesState,
  onSelectUpcomingMatch,
  onOpenRankingTab,
  memberCount,
}: {
  pouleId: number
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  bootstrap: ReturnType<typeof usePouleDashboardBootstrap>['data']
  liveMatches: ReturnType<typeof usePouleLiveMatches>['data']
  upcomingMatches: FootballMatch[]
  liveMatchesState: 'idle' | 'loading' | 'error' | 'ready'
  upcomingMatchesState: 'idle' | 'loading' | 'error' | 'ready'
  onSelectUpcomingMatch: (match: FootballMatch) => void
  onOpenRankingTab: () => void
  memberCount: number | undefined
}) {
  const { t } = useTranslation()
  if (isLoading) {
    return <PouleDashboardSkeleton />
  }

  if (isError || !bootstrap) {
    return (
      <InlineStatePanel
        title={t('Dashboard niet geladen')}
        body={t('De dashboard-tab kon niet worden opgebouwd met de huidige backenddata.')}
        tone="error"
        cta={t('Opnieuw proberen')}
        onClick={onRetry}
      />
    )
  }

  return (
    <PouleDashboardTab
      pouleId={pouleId}
      bootstrap={bootstrap}
      liveMatches={liveMatches ?? []}
      liveMatchesState={liveMatchesState}
      upcomingMatches={upcomingMatches}
      upcomingMatchesState={upcomingMatchesState}
      onSelectUpcomingMatch={onSelectUpcomingMatch}
      onOpenRankingTab={onOpenRankingTab}
      memberCount={memberCount}
    />
  )
}

function PouleDetailSkeleton() {
  const { t } = useTranslation()
  return (
    <div className="space-y-5" aria-label={t('Poule laden')}>
      <div className="h-44 animate-pulse rounded-[1.5rem] bg-[#0d1113]" />
      <div className="h-14 animate-pulse rounded-[1.25rem] bg-[#0d1113]" />
      <PouleDashboardSkeleton />
    </div>
  )
}

function PouleDashboardSkeleton() {
  const { t } = useTranslation()
  return (
    <div className="space-y-4" aria-label={t('Poule-dashboard laden')}>
      <div className="h-40 animate-pulse rounded-[1.5rem] bg-[#0d1113]" />
      <div className="grid gap-4 md:grid-cols-2">
        <div className="h-52 animate-pulse rounded-[1.5rem] bg-[#0d1113]" />
        <div className="h-52 animate-pulse rounded-[1.5rem] bg-[#0d1113]" />
      </div>
    </div>
  )
}

function InlineStatePanel({
  title,
  body,
  tone,
  cta,
  onClick,
}: {
  title: string
  body: string
  tone: 'neutral' | 'error'
  cta?: string
  onClick?: () => void
}) {
  const classes =
    tone === 'error'
      ? 'border-red-900/60 bg-red-950/25 text-red-100'
      : 'border-[#202427] bg-[#0a0d0f] text-surface-100'

  return (
    <section className={`rounded-[1.35rem] border p-5 ${classes}`}>
      <h2 className="text-xl font-black tracking-tight text-inherit">{title}</h2>
      <p className="mt-2 max-w-2xl text-sm leading-6 text-inherit/80">{body}</p>
      {cta && onClick ? (
        <button
          type="button"
          onClick={onClick}
          className="mt-4 rounded-full border border-current/20 px-4 py-2 text-sm font-semibold text-inherit transition hover:bg-white/5"
        >
          {cta}
        </button>
      ) : null}
    </section>
  )
}

function parsePouleId(value?: string) {
  if (!value) return null
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

function handleMatchSelection(
  action: MatchCardAction,
  pouleId: number | null,
  activeTab: string,
  navigate: ReturnType<typeof useNavigate>,
  setPredictionMatch: (match: FootballMatch | null) => void,
) {
  if (action.kind === 'predict') {
    setPredictionMatch(action.match)
    return
  }

  if (pouleId == null) return

  setPredictionMatch(null)
  const searchParams = new URLSearchParams()
  if (typeof action.match.poule_match_id === 'number') {
    searchParams.set('pouleMatchId', String(action.match.poule_match_id))
  }
  if (activeTab && activeTab !== 'dashboard') {
    searchParams.set('sourceTab', activeTab)
  }

  navigate(
    `/poules/${pouleId}/wedstrijden/${action.match.id}${searchParams.size > 0 ? `?${searchParams.toString()}` : ''}`,
  )
}

function getNextPoulePredictionMatch(
  matches: FootballMatch[],
  currentMatchId: number,
) {
  return (
    matches.find(
      (match) =>
        match.id !== currentMatchId &&
        canPredictMatch(match) &&
        (match.user_prediction?.home_pred == null ||
          match.user_prediction?.away_pred == null),
    ) ?? null
  )
}

function normalizeTabValue(value?: string | null) {
  const normalized = value?.trim().toLowerCase()
  if (normalized === 'groups') return 'groups'
  if (normalized === 'matches') return 'matches'
  if (normalized === 'results') return 'results'
  if (normalized === 'ranking') return 'ranking'
  return 'dashboard'
}
